import React from 'react';
import { PropTypes } from 'prop-types';
import { formatDateParsedZoneLong } from 'platform/utilities/date/index';
import KeyValueList from './KeyValueList';
import { INTENT_TO_FILE_FORM } from '../utilities/constants';

/**
 * The label for each VBMS eFolder status, which the submission cards and the
 * combined history table show.
 *
 * @type {ReadonlyMap<string, string>}
 */
export const VBMS_STATUS_LABELS = new Map([
  ['received', 'Received'],
  ['awaiting_receipt_warning', 'Processing error'],
  ['processing_error', 'Processing error'],
  ['awaiting_receipt', 'Awaiting receipt'],
]);

/**
 * @param {{ vbmsStatus: string }} submission
 * @returns {string} The label for the submission's VBMS eFolder status, or
 *   "Awaiting receipt" for a status the map does not list.
 */
export const vbmsStatusLabel = submission =>
  VBMS_STATUS_LABELS.get(submission.vbmsStatus) ??
  VBMS_STATUS_LABELS.get('awaiting_receipt');

/**
 * @param {{ vbmsStatus: string, vbmsReceivedDate?: string|null }} submission
 * @returns {JSX.Element} The VBMS eFolder status with its icon. A received
 *   submission shows its receipt date when the source reported one. An
 *   unrecognized status shows as awaiting receipt.
 */
const formatStatus = submission => {
  const label = vbmsStatusLabel(submission);

  switch (submission.vbmsStatus) {
    case 'received':
      return (
        <span>
          <va-icon
            class="submissions__inline-status-icon submissions__card-check"
            icon="check_circle"
            size="3"
          />
          {submission.vbmsReceivedDate
            ? `${label} ${formatDateParsedZoneLong(
                submission.vbmsReceivedDate,
              )}`
            : label}
        </span>
      );
    case 'awaiting_receipt_warning':
    case 'processing_error':
      return (
        <span>
          <va-icon
            icon="warning"
            class="submissions__inline-status-icon submissions__card-error"
            size="3"
            aria-hidden="true"
          />
          {` ${label}`}
        </span>
      );
    case 'awaiting_receipt':
    default:
      return (
        <span className="submissions__awaiting">
          <va-icon
            class="submissions__inline-status-icon submissions__card-check"
            icon="loop"
            size="3"
          />
          <span>{label}</span>
        </span>
      );
  }
};

const getFormName = formType => {
  switch (formType) {
    case '21-686c':
    case '21-526EZ':
      return `VA ${formType}`;
    case INTENT_TO_FILE_FORM:
      return `VA Form ${formType}`;
    default:
      return `VA ${formType}`;
  }
};

const getBenefitName = benefitType => {
  switch (benefitType) {
    case 'compensation':
      return 'Disability Compensation (VA Form 21-526EZ)';
    case 'pension':
      return 'Pension (VA Form 21P-527EZ)';
    case 'survivor':
      return 'Survivors pension and/or dependency and indemnity compensation (DIC) (VA Form 21P-534 or VA Form 21P-534EZ)';
    default:
      return '';
  }
};

/**
 * @param {{ formType: string, packet?: boolean }} submission
 * @returns {string[]} The form name, followed by " packet" for a packet.
 */
const formNameText = submission => [
  getFormName(submission.formType),
  submission.packet ? ' packet' : '',
];

const SubmissionCard = ({ submission, context }) => {
  const formattedSubmittedDate = formatDateParsedZoneLong(
    submission.submittedDate,
  );
  const isITF = submission.formType === INTENT_TO_FILE_FORM;
  const isWarning =
    submission.vbmsStatus === 'processing_error' ||
    submission.vbmsStatus === 'awaiting_receipt_warning';
  const cardData = isITF
    ? {
        Benefit: getBenefitName(submission.benefitType),
        'ITF Date': formattedSubmittedDate,
      }
    : {
        Confirmation: submission.confirmationNumber,
        'VBMS eFolder status': formatStatus(submission),
      };

  return (
    // eslint-disable-next-line jsx-a11y/no-redundant-roles
    <li role="listitem">
      <va-card class="submission__card">
        <h3
          aria-labelledby={`submissions-card-date-${
            submission.confirmationNumber
          } ${
            context === 'ClaimantSubmissionHistoryPage'
              ? ``
              : `submissions-card-name-${submission.confirmationNumber}`
          } submissions-card-form-${submission.confirmationNumber}`}
          className="vads-u-display--flex vads-u-flex-direction--column vads-u-margin-top--0"
        >
          <span
            id={`submissions-card-date-${submission.confirmationNumber}`}
            className="submission__card-date vads-u-font-weight--normal vads-u-font-family--sans"
          >
            Submitted {formattedSubmittedDate}
          </span>
          {context === 'ClaimantSubmissionHistoryPage' ? (
            <span
              id={`submissions-card-form-${submission.confirmationNumber}`}
              className="submission__card-form-name vads-u-font-size--h3"
            >
              {formNameText(submission)}
            </span>
          ) : (
            <>
              <span
                id={`submissions-card-name-${submission.confirmationNumber}`}
                className="submission__card-name vads-u-font-size--h3"
              >
                {`${submission.lastName}, ${submission.firstName}`}
              </span>
              <span
                id={`submissions-card-form-${submission.confirmationNumber}`}
                className="submission__card-form-name vads-u-font-size--h5"
              >
                {formNameText(submission)}
              </span>
            </>
          )}
        </h3>
        <KeyValueList data={cardData} />
        {!isITF && isWarning && (
          <p className="vads-u-margin-top--0p5">
            Contact 855-225-0709 for assistance
          </p>
        )}
        {context === 'ClaimantSubmissionHistoryPage' ? (
          ''
        ) : (
          <va-link
            active
            class="vads-u-margin-top--2 vads-u-display--block"
            label={
              submission.firstName && submission.lastName
                ? `Go to the claimant overview for ${submission.lastName}, ${submission.firstName}`
                : 'Go to the claimant overview'
            }
            href={`/representative/find-claimant/claimant-overview/${submission.claimantId}`}
            text="Go to the claimant overview"
          />
        )}
      </va-card>
    </li>
  );
};

SubmissionCard.propTypes = {
  context: PropTypes.string,
  cssClass: PropTypes.string,
  id: PropTypes.string,
  submission: PropTypes.object,
};

SubmissionCard.defaultProps = {
  context: null,
};

export default SubmissionCard;
